import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "./App.css";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Static critical layout components
import Home from "./components/home";
import NavbarComponent from "./components/header";
import Footer from "./components/footer";
import { isAuthenticated } from "./utils/auth";
import { ProtectedRoute, PublicRoute } from "./components/Routes";
import whatsappimg from "./assets/images/Group (2).png";

// Code-split route components (Loaded on demand)
const AboutUs = lazy(() => import("./components/aboutUs"));
const ContactUs = lazy(() => import("./components/ContactUs"));
const Products = lazy(() => import("./components/Products"));
const WishList = lazy(() =>
  import("./components/WishList").then((m) => ({ default: m.WishList }))
);
const ProductDetails = lazy(() => import("./components/ProductDetails"));
const Registeration = lazy(() => import("./components/Auth/Register"));
const Login = lazy(() => import("./components/Auth/Login"));
const ForgotPassword = lazy(() => import("./components/Auth/ForgotPassword"));
const ForgotPasswordOtp = lazy(() =>
  import("./components/Auth/ForgotPasswordOtp")
);
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const RegisterOtp = lazy(() => import("./components/Auth/RegisterOtp"));
const UpdatePassword = lazy(() => import("./components/Auth/UpdatePassword"));
const ShoppingCart = lazy(() => import("./components/Cart"));
const NotFoundPage = lazy(() => import("./PageNotFound"));
const ProfilePage = lazy(() => import("./components/profile/profile"));
const CheckoutPage = lazy(() => import("./components/CheckOut/CheckOut"));
const Invoice = lazy(() =>
  import("./components/profile/components/order-details")
);
const OrderSuccessPage = lazy(() => import("./components/OrderSuccess"));
const TermsPage = lazy(() => import("./components/TermsAndConditions"));
const PrivacyPolicyPage = lazy(() => import("./components/PrivacyPolicy"));
const ShippingDeliveryPage = lazy(() =>
  import("./components/ShippingDelivery")
);
const RefundPolicyPage = lazy(() => import("./components/RefundPolicy"));
const BlogsPage = lazy(() =>
  import("./components/Blogs").then((m) => ({ default: m.BlogsPage }))
);
const BlogDetailsPage = lazy(() =>
  import("./components/BlogDetails").then((m) => ({
    default: m.BlogDetailsPage,
  }))
);

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex flex-col items-center justify-center py-16">
      <div className="w-10 h-10 border-3 border-gray-200 border-t-[#77C712] rounded-full animate-spin"></div>
      <span className="mt-3 text-sm text-gray-500 font-medium tracking-wide">
        Loading...
      </span>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <MainLayout />
    </Router>
  );
}

function MainLayout() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {!isAdminRoute && <NavbarComponent />}

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product-details/:slug" element={<ProductDetails />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blog" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailsPage />} />
          <Route path="/blog-details/:slug" element={<BlogDetailsPage />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/shipping-policy" element={<ShippingDeliveryPage />} />
          <Route path="/return-refunds" element={<RefundPolicyPage />} />

          {/* PublicRoute wrapper → prevents access when logged in and pincode set */}
          <Route
            element={
              <PublicRoute
                isAuthenticated={
                  isAuthenticated() && Boolean(localStorage.getItem("user_pincode"))
                }
              />
            }
          >
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Registeration />} />
            <Route path="/register-otp" element={<RegisterOtp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/forgot-password-otp"
              element={<ForgotPasswordOtp />}
            />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* ProtectedRoute wrapper */}
          <Route
            element={<ProtectedRoute isAuthenticated={isAuthenticated()} />}
          >
            <Route path="/wish-list" element={<WishList />} />
            <Route path="/cart" element={<ShoppingCart />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-details/:id" element={<Invoice />} />
            <Route path="/order-success" element={<OrderSuccessPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {!isAdminRoute && <Footer />}

      <a
        href="https://wa.me/919915750008"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-button"
        aria-label="Contact us on WhatsApp"
      >
        <img
          src={whatsappimg}
          alt="WhatsApp"
          loading="lazy"
          decoding="async"
          className="w-12 h-12 object-contain"
        />
      </a>
    </>
  );
}

export default App;
